import { ErrorHandler, importProvidersFrom, inject, Injectable, NgModule } from '@angular/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NgxPermissionsModule } from 'ngx-permissions';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { AgmMapModule } from 'agm-map';
import { ApmModule, ApmService } from '@elastic/apm-rum-angular';
import * as Sentry from '@sentry/angular';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { environment } from 'src/environments/environment';
import { ConnectionErrorComponent } from './otherpage/connection-error/connection-error/connection-error.component';
import { ConnectionSuccessComponent } from './otherpage/connectionsuccess/connection-success/connection-success.component';
import { XLoaderModule } from './x-loader/x-loader.module';
import { SharedModule } from './shared/sharedModule/sharedModule.module';
import { Page404Component } from './otherpage/page404/page404.component';
import { AppRoutingModule } from './app-routing.module';
import { OAuthCheckModule } from './login/oauthCheck/oauthCheck.module';
import { configurationsReplace } from '../assets/data/configurationsReplace';
import { ThemeConfig } from '../assets/data/enum/themeConfig';
import { Page401Component } from './otherpage/page401/page401.component';
import { DownloadAppOverlayModule } from './shared/download-app-overlay/download-app-overlay.module';
import { PublicChatSupportModule } from './shared/public-chat-support/public-chat-support.module';
import { XInformationBarModule } from './shared/x-information-bar/x-information-bar.module';
import { CoreModule } from './shared/coreModule/coreModule';
import { JwtInterceptor } from './common/_interceptors/jwt.interceptor';
import { GoogleMapsLoaderService } from './common/_services/google/google-maps-loader.service';
import { GOOGLE_MAPS_CONFIG } from './common/_services/google/google-maps-config';
import { FeatureFlagService } from './common/_services/featureFlag.service';
import { ThemeService } from './common/_theme/theme.service';
import { ApmEventsService } from './common/_services/apmEvents.service';

@Injectable()
export class CustomErrorHandler implements ErrorHandler {
  private readonly sentryHandler = Sentry.createErrorHandler({ showDialog: false });
  private readonly apmService = inject(ApmService);

  handleError(error: unknown): void {
    // Handle error with APM if enabled
    if (environment.themeConfig.enableApm) {
      this.apmService.apm.captureError(
        error instanceof Error ? error : new Error(JSON.stringify(error))
      );
    }

    // Handle error with Sentry only for specific brand
    else if (environment.themeConfig.brand === 'xFarm') {
      this.sentryHandler.handleError(error);
    }
  }
}

export class CustomTranslateLoader implements TranslateLoader {
  constructor(
    private httpClient: HttpClient,
    private themeService: ThemeService
  ) {}

  public getTranslation(lang: string): Observable<any> {
    const version = this.themeService.getLocalVersion(true);
    const url = `assets/i18n/${lang}.json?version=${version}`;
    // X-Skip-Interceptor to ignore JwtInterceptor
    const headers = new HttpHeaders().set('X-Skip-Interceptor', 'true');

    return this.httpClient.get(url, { headers }).pipe(
      map((translation: any) => {
        if (configurationsReplace && configurationsReplace[ThemeConfig.brand]) {
          for (const config of configurationsReplace[ThemeConfig.brand]) {
            replaceValues(translation, config.path, config.replacements);
          }
        }
        return translation;
      })
    );
  }
}

export function HttpLoaderFactory(httpClient: HttpClient, themeService: ThemeService) {
  return new CustomTranslateLoader(httpClient, themeService);
}

function replaceValues(data: any, path: string, replacements: { [key: string]: string }): void {
  const keys = path.split('.');
  let target = data;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (target[key] === undefined) {
      return;
    }
    target = target[key];
  }

  const lastKey = keys[keys.length - 1];
  if (typeof target[lastKey] === 'string') {
    target[lastKey] = applyReplacements(target[lastKey], replacements);
  } else if (Array.isArray(target[lastKey])) {
    target[lastKey] = target[lastKey].map((item: string) => applyReplacements(item, replacements));
  } else if (typeof target[lastKey] === 'object') {
    for (const key in target[lastKey]) {
      if (Object.prototype.hasOwnProperty.call(target[lastKey], key)) {
        target[lastKey][key] = applyReplacements(target[lastKey][key], replacements);
      }
    }
  }
}

function applyReplacements(text: string, replacements: { [key: string]: string }): string {
  const regex = new RegExp(`\\b(${Object.keys(replacements).join('|')})\\b`, 'g');
  return text.replace(regex, (match: string) => replacements[match] || match);
}

@NgModule({
  declarations: [
    Page404Component,
    Page401Component,
    ConnectionSuccessComponent,
    ConnectionErrorComponent,
  ],
  imports: [
    CoreModule.forRoot(),
    SharedModule,
    AppRoutingModule,
    XLoaderModule,
    AgmMapModule.forRoot({
      apiKey: environment.googleApiKey,
      libraries: ['drawing', 'places', 'geometry', 'visualization'],
    }),
    NgxPermissionsModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient, ThemeService],
      },
    }),
    XInformationBarModule,
    OAuthCheckModule,
    PublicChatSupportModule,
    DownloadAppOverlayModule,
  ],
  providers: [
    importProvidersFrom(ApmModule),
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler,
    },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    BsLocaleService,
    FeatureFlagService,
    GoogleMapsLoaderService,
    {
      provide: GOOGLE_MAPS_CONFIG,
      useValue: {
        apiKey: environment.googleApiKey,
        libraries: ['drawing', 'places', 'geometry', 'visualization'],
      },
    },
    provideAnimationsAsync(),
  ],
})
export class AppModule {
  private readonly apmEventsService = inject(ApmEventsService);
  constructor() {
    // Initialize APM events service
    this.apmEventsService.init();
    this.apmEventsService.setInitialApmLabels();
  }
}
